import React from 'react'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import {
  MoreHorizontal,
  Shield,
  ShieldCheck,
  ShieldX,
  Power,
  PowerOff,
  Trash2
} from 'lucide-react'
import type { UserListItem } from '@/features/users/types'

interface UsersTableProps {
  users: UserListItem[]
  currentUserId?: string
  onEdit: (user: UserListItem) => void
  onToggleStatus: (userId: string, isActive: boolean) => void
  onDelete: (userId: string) => void
}

function getRoleIcon(roleName: string) {
  switch (roleName) {
    case 'admin':
      return <ShieldCheck className="h-4 w-4 text-red-500" />
    case 'manager':
      return <Shield className="h-4 w-4 text-blue-500" />
    case 'viewer':
      return <ShieldX className="h-4 w-4 text-gray-500" />
    default:
      return <Shield className="h-4 w-4 text-gray-400" />
  }
}

function getRoleBadgeColor(roleName: string): 'destructive' | 'default' | 'secondary' | 'outline' {
  switch (roleName) {
    case 'admin':
      return 'destructive'
    case 'manager':
      return 'default'
    case 'viewer':
      return 'secondary'
    default:
      return 'outline'
  }
}

export function UsersTable({ users, currentUserId, onEdit, onToggleStatus, onDelete }: UsersTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>User</TableHead>
          <TableHead>Role</TableHead>
          <TableHead>Franchises</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Last Login</TableHead>
          <TableHead>Created</TableHead>
          <TableHead className="w-[70px]">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.map((user) => {
          const triggerLabel = `Open user actions for ${user.full_name || user.email}`
          const initials = user.full_name?.split(' ').map(n => n[0]).join('') || user.email.substring(0, 2).toUpperCase()

          return (
            <TableRow key={user.id}>
              <TableCell className="flex items-center gap-3">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user.avatar_url || ''} />
                  <AvatarFallback>{initials}</AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-medium">{user.full_name || 'No name'}</div>
                  <div className="text-sm text-gray-500">{user.email}</div>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {getRoleIcon(user.role_name)}
                  <Badge variant={getRoleBadgeColor(user.role_name)}>
                    {user.role_name}
                  </Badge>
                </div>
              </TableCell>
              <TableCell>
                {user.assigned_franchises?.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {user.assigned_franchises.slice(0, 2).map((franchise) => (
                      <Badge key={franchise} variant="outline" className="text-xs">
                        {franchise}
                      </Badge>
                    ))}
                    {user.assigned_franchises.length > 2 && (
                      <Badge variant="outline" className="text-xs">
                        +{user.assigned_franchises.length - 2}
                      </Badge>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-gray-500">All franchises</span>
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {user.is_active ? (
                    <Power className="h-4 w-4 text-green-500" />
                  ) : (
                    <PowerOff className="h-4 w-4 text-red-500" />
                  )}
                  <span className={user.is_active ? 'text-green-700' : 'text-red-700'}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                {user.last_sign_in_at ? (
                  <span className="text-sm">
                    {new Date(user.last_sign_in_at).toLocaleDateString()}
                  </span>
                ) : (
                  <span className="text-sm text-gray-500">Never</span>
                )}
              </TableCell>
              <TableCell>
                <span className="text-sm">
                  {new Date(user.profile_created_at).toLocaleDateString()}
                </span>
              </TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      aria-label={triggerLabel}
                    >
                      <span className="sr-only">{triggerLabel}</span>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                    <DropdownMenuItem
                      data-testid={`users-actions-edit-${user.id}`}
                      onClick={() => onEdit(user)}
                    >
                      Edit User
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      data-testid={`users-actions-toggle-${user.id}`}
                      onClick={() => onToggleStatus(user.id, user.is_active)}
                    >
                      {user.is_active ? 'Deactivate' : 'Activate'}
                    </DropdownMenuItem>
                    {currentUserId !== user.id && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          data-testid={`users-actions-delete-${user.id}`}
                          className="text-red-600"
                          onClick={() => onDelete(user.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete User
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
